import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { api, type ChatMessage, type User } from '../lib/api';

interface ChatHistoryProps {
  user: User;
  escalationId: string; // Required: Shows escalation context and specific conversation
  isResolved?: boolean; // If true, shows full history including expert responses
}

export const ChatHistory = ({ user, escalationId, isResolved = false }: ChatHistoryProps) => {
  const [allMessages, setAllMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get escalation context - full history if resolved, pre-escalation context otherwise
  const { data: escalationContextData } = useQuery({
    queryKey: ['escalationContext', escalationId, isResolved],
    queryFn: () => {
      // For resolved escalations, get full history including expert responses
      if (isResolved) {
        return api.getEscalationFullHistory(escalationId!);
      }
      // For active escalations, get pre-escalation context only
      return api.getEscalationChatContext(escalationId!, 10);
    },
    enabled: !!escalationId,
  });

  // No conversation switching - we only show escalation context

  // Get messages for escalation context only
  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['escalationMessages', escalationId, isResolved],
    queryFn: async () => {
      if (escalationContextData) {
        return { messages: escalationContextData.messages };
      }
      return { messages: [] };
    },
    refetchInterval: isResolved ? undefined : 5000, // Don't auto-refresh for resolved escalations
    enabled: !!escalationContextData,
  });

  // Message sending is handled by EscalationDetail page response editor

  // No clear history in escalation context view - read-only

  useEffect(() => {
    if (messagesData?.messages) {
      // Messages are now returned in correct chronological order from backend
      setAllMessages(messagesData.messages);
    }
  }, [messagesData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages]);

  // Message sending removed - handled by parent EscalationDetail page

  const formatDateSeparator = (dateKey: string) => {
    // dateKey is from toDateString(), so we can use it directly for comparison
    const today = new Date().toDateString();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toDateString();

    if (dateKey === today) {
      return 'Today';
    } else if (dateKey === yesterdayString) {
      return 'Yesterday';
    } else {
      // Parse the dateKey back to a Date for formatting
      const date = new Date(dateKey);
      if (isNaN(date.getTime())) {
        console.warn('formatDateSeparator: Invalid dateKey:', dateKey);
        return 'Invalid date';
      }
      try {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } catch (error) {
        console.error('formatDateSeparator: Error formatting date:', error, 'for dateKey:', dateKey);
        return 'Format error';
      }
    }
  };

  const formatTime = (dateString: string | null | undefined) => {
    // Handle null, undefined, or empty strings
    if (!dateString || typeof dateString !== 'string') {
      console.warn('formatTime: Invalid dateString type:', typeof dateString, dateString);
      return 'No time';
    }
    
    try {
      // Try multiple date parsing approaches
      let date: Date;
      
      // First, try direct parsing
      date = new Date(dateString);
      
      // If that fails, try parsing as ISO string explicitly
      if (isNaN(date.getTime())) {
        date = new Date(dateString.replace('Z', '+00:00'));
      }
      
      // Final validation
      if (isNaN(date.getTime())) {
        console.warn('formatTime: Could not parse date string:', dateString);
        return 'Invalid time';
      }
      
      // Format the time
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.error('formatTime: Exception while formatting:', error, 'for date:', dateString);
      return 'Format error';
    }
  };

  const groupMessagesByDate = (messages: ChatMessage[]) => {
    const groups: { [key: string]: ChatMessage[] } = {};
    
    messages.forEach((message) => {
      if (!message.createdAt) {
        console.warn('groupMessagesByDate: Message missing createdAt:', message);
        return;
      }
      
      const date = new Date(message.createdAt);
      if (isNaN(date.getTime())) {
        console.warn('groupMessagesByDate: Invalid date found in message:', message.createdAt, 'for message:', message);
        return; // Skip messages with invalid dates
      }
      
      const dateKey = date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });

    // Sort by the actual date, not the string representation
    return Object.entries(groups).sort(([a], [b]) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA.getTime() - dateB.getTime();
    });
  };

  const messageGroups = groupMessagesByDate(allMessages);

  return (
    <div className="flex flex-col h-full w-full bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="flex items-center space-x-4 p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate">{user.displayName}</h2>
            <p className="text-blue-100 text-sm">{user.phone}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-white text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${
              isResolved ? 'bg-green-600' : 'bg-yellow-500'
            }`}>
              {isResolved ? 'Full Escalation History' : 'Pre-Escalation Context'}
            </span>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading messages...</p>
              </div>
            </div>
          ) : messageGroups.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-xl text-gray-500">No messages yet</p>
                <p className="text-gray-400 mt-2">Start a conversation with {user.displayName}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messageGroups.map(([dateKey, messages]) => (
                <div key={dateKey}>
                  {/* Date Separator */}
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-gray-600 shadow-sm">
                      {formatDateSeparator(dateKey)}
                    </div>
                  </div>

                  {/* Messages for this date */}
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.messageType === 'user' ? 'justify-start' : 'justify-end'}`}
                      >
                        {/* For user messages: avatar first, then message content (left side) */}
                        {message.messageType === 'user' ? (
                          <div className="flex items-start space-x-2 max-w-4xl">
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div
                              className="px-4 py-3 rounded-2xl shadow-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-bl-md"
                            >
                              <div className="prose prose-sm prose-invert max-w-none">
                                <ReactMarkdown
                                  components={{
                                    p: ({children}) => <p className="text-white text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
                                    strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                                    em: ({children}) => <em className="text-blue-100">{children}</em>,
                                    code: ({children}) => <code className="bg-blue-700 bg-opacity-50 px-1 py-0.5 rounded text-blue-100 text-xs font-mono">{children}</code>,
                                    pre: ({children}) => <pre className="bg-blue-700 bg-opacity-50 p-2 rounded text-blue-100 text-xs overflow-x-auto font-mono my-2">{children}</pre>,
                                    ul: ({children}) => <ul className="list-disc pl-4 mb-2 text-white">{children}</ul>,
                                    ol: ({children}) => <ol className="list-decimal pl-4 mb-2 text-white">{children}</ol>,
                                    li: ({children}) => <li className="mb-1 text-white">{children}</li>,
                                  }}
                                >
                                  {message.content}
                                </ReactMarkdown>
                              </div>

                              <p className="text-xs mt-2 text-blue-100">
                                {formatTime(message.createdAt)}
                              </p>
                            </div>
                          </div>
                        ) : (
                          /* For assistant/expert messages: message content first, then avatar (right side) */
                          <div className="flex items-start space-x-2 max-w-4xl flex-row-reverse space-x-reverse">
                            
                            {/* Avatar for assistant and expert messages on the right */}
                            {message.messageType === 'assistant' && (
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            {message.messageType === 'expert' && (
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                            )}
                            
                            <div
                              className={`px-4 py-3 rounded-2xl shadow-sm ${
                                message.messageType === 'expert'
                                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-900 border border-green-200 rounded-br-md'
                                  : 'bg-gray-100 text-gray-900 rounded-br-md'
                              }`}
                            >
                              {/* Message content with markdown support */}
                              <div className="prose prose-sm max-w-none">
                                <ReactMarkdown
                                  components={{
                                    p: ({children}) => <p className={`text-sm leading-relaxed mb-2 last:mb-0 ${
                                      message.messageType === 'expert' ? 'text-green-900' : 'text-gray-900'
                                    }`}>{children}</p>,
                                    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                                    em: ({children}) => <em className={message.messageType === 'expert' ? 'text-green-700' : 'text-gray-700'}>{children}</em>,
                                    code: ({children}) => <code className={`px-1 py-0.5 rounded text-xs font-mono ${
                                      message.messageType === 'expert' 
                                        ? 'bg-green-200 text-green-800' 
                                        : 'bg-gray-200 text-gray-800'
                                    }`}>{children}</code>,
                                    pre: ({children}) => <pre className={`p-2 rounded text-xs overflow-x-auto font-mono my-2 ${
                                      message.messageType === 'expert'
                                        ? 'bg-green-200 text-green-800'
                                        : 'bg-gray-200 text-gray-800'
                                    }`}>{children}</pre>,
                                    ul: ({children}) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                                    ol: ({children}) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                                    li: ({children}) => <li className="mb-1">{children}</li>,
                                  }}
                                >
                                  {message.content}
                                </ReactMarkdown>
                              </div>
                              
                              {/* Simple confidence score for assistant messages */}
                              {/* {message.messageType === 'assistant' && message.confidenceScore !== undefined && (
                                <div className={`mt-2 inline-flex items-center text-xs px-2 py-1 rounded ${
                                  message.confidenceScore < 3 
                                    ? 'bg-red-50 text-red-600' 
                                    : message.confidenceScore < 7 
                                    ? 'bg-yellow-50 text-yellow-600'
                                    : 'bg-green-50 text-green-600'
                                }`}>
                                  {message.confidenceScore < 3 ? (
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.866-.833-2.635 0L4.179 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                  ) : message.confidenceScore < 7 ? (
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                  Confidence: {message.confidenceScore}/10 ({message.messageType === 'assistant' ? 'Assistant' : 'Expert'}) 
                                </div>
                              )} 
                              
                              */}
                              
                              {/* Expert badge for expert messages */}
                              {message.messageType === 'expert' && (
                                <div className="mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded inline-flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Expert Response
                                </div>
                              )}
                              
                              {/* Timestamp */}
                              <p className="text-xs mt-2 text-gray-500">
                                {formatTime(message.createdAt)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

    </div>
  );
};
